// One shared download across mounts. A failed attempt must not strand a script
// tag that prevents every later Map visit from trying again.
export const MAP_SCRIPT_TIMEOUT_MS = 12000;
let pending = null;
let sequence = 0;

export function subscribeToGoogleMaps({ apiKey, onReady, onError }) {
  if (typeof window.google?.maps?.Map === 'function') {
    onReady();
    return () => {};
  }
  const subscriber = { onReady, onError };
  if (!pending) {
    const script = document.createElement('script');
    const callbackName = `__sitescopeMapsReadyV1_${++sequence}`;
    const load = { subscribers: new Set(), script };
    pending = load;
    let settled = false;
    const finish = error => {
      if (settled) return;
      settled = true;
      clearTimeout(load.timer);
      script.onerror = null;
      pending = null;
      if (error) script.remove();
      // A script removed after a timeout may still execute late. Its unique
      // callback cannot complete a newer attempt or notify unmounted consumers.
      if (error) window[callbackName] = () => { delete window[callbackName]; };
      else delete window[callbackName];
      const subscribers = [...load.subscribers];
      load.subscribers.clear();
      subscribers.forEach(listener => error ? listener.onError() : listener.onReady());
    };
    window[callbackName] = () => finish(typeof window.google?.maps?.Map !== 'function');
    script.onerror = () => finish(true);
    script.dataset.gmap = '1';
    const params = new URLSearchParams({ key: apiKey, libraries: 'places', loading: 'async', callback: callbackName });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    load.timer = setTimeout(() => finish(true), MAP_SCRIPT_TIMEOUT_MS);
    load.subscribers.add(subscriber);
    document.head.appendChild(script);
    return () => load.subscribers.delete(subscriber);
  }
  const load = pending;
  load.subscribers.add(subscriber);
  return () => load.subscribers.delete(subscriber);
}
